import { Check } from "lucide-react";
import { Reveal } from "./reveal";
import { LazyMount } from "./lazy-mount";
import badge from "../assets/raindrop/raindrop-certified-installer.jpg.asset.json";
import logo from "../assets/raindrop/raindrop-logo.jpg.asset.json";
import overview from "../assets/raindrop/raindrop-overview.mp4.asset.json";
import heavyRain from "../assets/raindrop/raindrop-heavy-rain.mp4.asset.json";
import mapleSeeds from "../assets/raindrop/raindrop-maple-seeds.mp4.asset.json";
import dryDebris from "../assets/raindrop/raindrop-dry-debris.mp4.asset.json";
import impact from "../assets/raindrop/raindrop-impact.mp4.asset.json";
import log from "../assets/raindrop/raindrop-log.mp4.asset.json";
import selfCleaning from "../assets/raindrop/raindrop-self-cleaning.mp4.asset.json";
import shedsIce from "../assets/raindrop/raindrop-sheds-ice.mp4.asset.json";
import sledgeHammer from "../assets/raindrop/raindrop-sledge-hammer.mp4.asset.json";
import wetDebris from "../assets/raindrop/raindrop-wet-debris.mp4.asset.json";

const CLIPS = [
  { src: overview.url, title: "How RainFlow Technology Works", desc: "Water follows the curve into the gutter while leaves and debris ride right over the edge." },
  { src: heavyRain.url, title: "Handles Heavy Rain", desc: "Built for worst-case downpours and high-volume roof valleys where other guards overshoot." },
  { src: mapleSeeds.url, title: "No Clogging From Maple Seeds", desc: "Helicopters and pine needles shed off the top instead of packing into a micro screen." },
  { src: dryDebris.url, title: "Dry Debris Blows Off", desc: "Once debris dries out, wind clears the guard — that's the self-cleaning design." },
  { src: wetDebris.url, title: "Wet Debris Sheds Too", desc: "Soaked leaves slide off the curved surface instead of matting down on a screen." },
  { src: selfCleaning.url, title: "Self-Cleaning Design", desc: "The RainFlow profile keeps debris moving on its own — no annual gutter cleanout." },
  { src: shedsIce.url, title: "Sheds Ice", desc: "The black surface absorbs winter sun so ice and snow release instead of building up." },
  { src: impact.url, title: "Impact Resistance", desc: "UV-stabilized polypropylene flexes under impact instead of denting like aluminum." },
  { src: sledgeHammer.url, title: "Sledgehammer Test", desc: "A direct sledgehammer hit — the guard flexes and springs back." },
  { src: log.url, title: "Damage Resilient", desc: "Even a heavy falling limb doesn't collapse the system the way flimsy covers do." },
];

const FEATURES = [

  { title: "Fits Every Home", desc: "Works with every roof style, fits 5\" and 6\" gutters, and can be modified for 4\"–10\" gutters thanks to the Flex-Flange design." },
  { title: "Handles All The Water", desc: "RainDrop is engineered to take any amount of water from the worst downpour — including high-volume valleys." },
  { title: "Strong & Long Lasting", desc: "A unique blend of UV-stabilized polypropylene that stands up to Capital Region winters, summer heat, and heavy storms." },
  { title: "Virtually Invisible", desc: "Black profile reads as nothing more than a shadow at the roof line — no bulky solid hoods on your house." },
  { title: "Helps Control Ice", desc: "The black surface absorbs winter sun, helping melt snow and ice so water keeps moving into the gutter." },
  { title: "Complete System", desc: "End caps, miter connectors, wide-mouth outlets, and downspout clean-outs finish the system properly — not just panels." },
];

function ClipCard({ src, title, desc }: { src: string; title: string; desc: string }) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="aspect-video bg-[var(--brand-deep-blue)]/5">
        <video
          src={src}
          className="h-full w-full object-cover"
          muted
          loop
          playsInline
          autoPlay
          preload="metadata"
          aria-label={`RainDrop Pro Gutter Guard demonstration: ${title}`}
        />
      </div>
      <div className="p-4">
        <h3
          className="text-[var(--brand-deep-blue)] text-base uppercase"
          style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
        >
          {title}
        </h3>
        <p className="mt-1 text-sm leading-relaxed text-[var(--brand-charcoal)]/80">{desc}</p>
      </div>
    </div>
  );
}

export function RaindropSection() {
  return (
    <section className="bg-white py-16 md:py-20" aria-labelledby="raindrop-title">
      <div className="mx-auto max-w-[1200px] px-4">
        <div className="grid items-center gap-10 md:grid-cols-[minmax(0,1fr)_260px]">
          <div>
            <p
              className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-[var(--brand-red)]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Certified Installer
            </p>
            <h2
              id="raindrop-title"
              className="text-3xl md:text-4xl uppercase leading-tight text-[var(--brand-deep-blue)]"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              We Install RainDrop<sup className="text-base align-super">®</sup> Pro Gutter Guard
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-[var(--brand-charcoal)]/85">
              Capital Pro is a certified RainDrop® Pro Gutter Guard installer. RainDrop isn't a
              micro-screen that eventually blinds over with shingle grit and pollen — it's an open
              RainFlow design that lets water in and keeps leaves, needles, and maple seeds moving
              off the roof line. First installed in 2000, with a track record of zero BBB complaints.
            </p>
            <img
              src={logo.url}
              alt="RainDrop Pro Gutter Guard logo"
              className="mt-6 h-10 w-auto"
              loading="lazy"
              width={480}
              height={104}
            />
          </div>
          <div className="justify-self-center">
            <img
              src={badge.url}
              alt="Certified RainDrop Pro Gutter Guard Installer badge"
              className="h-auto w-[200px] md:w-[240px]"
              loading="lazy"
              width={240}
              height={182}
            />
          </div>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <Reveal
              key={f.title}
              delayMs={i * 70}
              className="card-pop rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-6"
            >
              <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-md bg-[var(--brand-red)]/10 text-[var(--brand-red)]">
                <Check size={18} strokeWidth={3} />
              </div>
              <h3
                className="mb-2 text-lg uppercase text-[var(--brand-deep-blue)]"
                style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
              >
                {f.title}
              </h3>
              <p className="text-sm leading-relaxed text-[var(--brand-charcoal)]/85">{f.desc}</p>
            </Reveal>
          ))}
        </div>

        <div className="mt-14">
          <h3
            className="text-center text-2xl md:text-3xl uppercase text-[var(--brand-deep-blue)]"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            See RainDrop In Action
          </h3>
          <p className="mx-auto mt-3 max-w-[640px] text-center text-[var(--brand-charcoal)]/80">
            Manufacturer demonstrations of the same system we install on Capital Region homes.
          </p>
          <LazyMount minHeight={320} className="mx-auto mt-8 max-w-[900px]">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-black shadow-sm">
              <video
                className="aspect-video h-full w-full"
                controls
                playsInline
                preload="none"
                controlsList="nodownload"
                poster="https://a80427d48f9b9f165d8d-c913073b3759fb31d6b728a919676eab.ssl.cf1.rackcdn.com/universal/videos/raindrop-official-video-thumbnail.jpg"
                src="https://a80427d48f9b9f165d8d-c913073b3759fb31d6b728a919676eab.ssl.cf1.rackcdn.com/universal/videos/RainDrop-Sales-V2.mp4"
                aria-label="RainDrop Pro Gutter Guard official product video"
              />
            </div>
            <p className="mt-3 text-center text-sm text-[var(--brand-charcoal)]/70">
              Official RainDrop Pro Gutter Guard product video
            </p>
          </LazyMount>
          <LazyMount minHeight={600} className="mt-8">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {CLIPS.map((c) => (
                <ClipCard key={c.title} {...c} />
              ))}
            </div>
          </LazyMount>
        </div>
      </div>
    </section>
  );
}
